import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  CloseOutlined,
  ErrorOutline,
  ImageOutlined,
  StarOutlineRounded,
} from '@mui/icons-material';

import { MAX_IMAGE_BYTES, ProductServices } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import {
  CoverButton,
  DropZone,
  ErrorBanner,
  FieldLabel,
  HelperText,
  RemoveButton,
  ThumbCard,
  ThumbGrid,
  ThumbTag,
  UploadingTile,
} from './admin.styled';

interface Props {
  /** Server paths already on the product — the first one is the cover. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

const megabytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

/**
 * Uploads to POST /api/upload and keeps the returned server paths in the form.
 * Files go up as soon as they are picked, so a product is only ever saved with
 * paths the server has confirmed.
 */
const ImageField = ({ value, onChange, disabled }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  const handleFiles = async (fileList: FileList | null) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setError('');

    // Multer accepts anything, so the guard rails live here.
    const tooBig = files.filter((f) => f.size > MAX_IMAGE_BYTES);
    const wrongType = files.filter((f) => !f.type.startsWith('image/'));
    const accepted = files.filter((f) => f.type.startsWith('image/') && f.size <= MAX_IMAGE_BYTES);

    if (wrongType.length > 0) {
      setError(`${wrongType.map((f) => f.name).join(', ')} — only image files can be uploaded.`);
    } else if (tooBig.length > 0) {
      setError(
        `${tooBig.map((f) => f.name).join(', ')} — each image must be under ${megabytes(
          MAX_IMAGE_BYTES
        )} MB.`
      );
    }

    if (accepted.length === 0) return;

    // One request per file; the endpoint takes a single `image` each time.
    // `pending` tracks what is still in flight so a throw mid-batch clears the
    // right number of placeholder tiles.
    let pending = accepted.length;
    const paths: string[] = [];
    setUploading((n) => n + pending);

    try {
      for (const file of accepted) {
        const path = await ProductServices.uploadImage(file);
        pending -= 1;
        setUploading((n) => Math.max(0, n - 1));
        if (path) paths.push(path);
      }
    } catch (err: any) {
      setError(err?.message || 'Could not upload that image.');
      setUploading((n) => Math.max(0, n - pending));
    } finally {
      // Keep whatever did make it up, even if a later file failed.
      if (paths.length > 0) onChange([...value, ...paths]);
    }
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Let the same file be picked again after it was removed.
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) handleFiles(e.dataTransfer.files);
  };

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  /** Promote an image to first position — that is what the storefront shows. */
  const makeCover = (index: number) => {
    const next = [...value];
    const [picked] = next.splice(index, 1);
    onChange([picked, ...next]);
  };

  const busy = disabled || uploading > 0;

  return (
    <div>
      <FieldLabel>Images</FieldLabel>

      {error && (
        <ErrorBanner role="alert">
          <ErrorOutline fontSize="small" />
          {error}
        </ErrorBanner>
      )}

      {(value.length > 0 || uploading > 0) && (
        <ThumbGrid>
          {value.map((path, index) => (
            <ThumbCard key={`${path}-${index}`} $cover={index === 0}>
              <img src={resolveImage(path)} alt={`Product ${index + 1}`} />

              {index === 0 ? (
                <ThumbTag>Cover</ThumbTag>
              ) : (
                <CoverButton
                  type="button"
                  title="Use as cover"
                  onClick={() => makeCover(index)}
                  disabled={busy}
                >
                  <StarOutlineRounded sx={{ fontSize: 15 }} />
                </CoverButton>
              )}

              <RemoveButton
                type="button"
                $tone="danger"
                title="Remove image"
                onClick={() => removeAt(index)}
                disabled={busy}
              >
                <CloseOutlined sx={{ fontSize: 14 }} />
              </RemoveButton>
            </ThumbCard>
          ))}

          {Array.from({ length: uploading }, (_, i) => (
            <UploadingTile key={`uploading-${i}`}>
              <CircularProgress size={20} sx={{ color: '#6c9284' }} />
            </UploadingTile>
          ))}
        </ThumbGrid>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleInput}
      />

      <DropZone
        $active={dragging}
        role="button"
        tabIndex={0}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !busy) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <ImageOutlined sx={{ fontSize: 22, color: '#6c9284' }} />
        <strong>{uploading > 0 ? 'Uploading…' : 'Drop images here or browse'}</strong>
        <span>JPG, PNG or WEBP — up to {megabytes(MAX_IMAGE_BYTES)} MB each</span>
      </DropZone>

      <HelperText>
        The first image is the cover shown on the shop. Use the star to promote another one.
      </HelperText>
    </div>
  );
};

export default ImageField;
